import Cookies from 'js-cookie';
import { NextRequest, NextResponse } from 'next/server';
import type AuthenticationType from '@alltrails/types/AuthenticationType';
import type CurrencyCode from '@alltrails/types/CurrencyCode';
import OsanoConsentObject from '@alltrails/types/OsanoConsentObject';
import { marketingCookieAttributes, NINETY_DAYS_MS } from './marketingCookies';

const CLICK_ID_QUERY_PARAM_NAME = 'fbclid';
export const CLICK_ID_COOKIE_NAME = '_fbc';
export const BROWSER_ID_COOKIE_NAME = '_fbp';

// FB Pixel

export const getFacebookConsentValue = (osanoConsentObject: OsanoConsentObject) => {
  return osanoConsentObject.MARKETING === 'ACCEPT' ? 'grant' : 'revoke';
};

export const setFacebookConsent = (osanoConsentObject: OsanoConsentObject) => {
  if (isFacebookPixelUnavailable()) {
    return;
  }

  fbq('consent', getFacebookConsentValue(osanoConsentObject));
};

// https://developers.facebook.com/docs/meta-pixel/reference#:~:text=PAGE_VIEW-,Purchase,-When%20a%20purchase
export const trackPurchaseForFacebook = ({
  shouldReInitializePixel,
  userId,
  emailSha256,
  currencyCode,
  price,
  subscription_event_id,
  recurlyPlanCode
}: {
  shouldReInitializePixel: boolean;
  userId: number;
  emailSha256?: string | null;
  currencyCode: CurrencyCode;
  price: number;
  subscription_event_id?: number;
  recurlyPlanCode: string;
}) => {
  if (isFacebookPixelUnavailable()) {
    return;
  }

  if (shouldReInitializePixel) {
    initFacebookPixel(userId, emailSha256);
  }

  fbq(
    'track',
    'Purchase',
    {
      currency: currencyCode,
      value: price,
      content_type: 'product',
      content_ids: [recurlyPlanCode]
    },
    { eventID: subscription_event_id?.toString() }
  );
};

// https://developers.facebook.com/docs/meta-pixel/reference#:~:text=ADD_TO_WISHLIST-,CompleteRegistration,-When%20a%20registration
export const trackCompleteRegistrationForFacebook = (userId: number, emailSha256: string | null, authType: AuthenticationType) => {
  if (isFacebookPixelUnavailable()) {
    return;
  }

  initFacebookPixel(userId, emailSha256);
  fbq('track', 'CompleteRegistration', { content_name: `signup - ${authType}` }, { eventID: `reg_${userId}` });
};

/**
 * Sets the _fbc and _fbp cookies based on the presence of the fbclid query parameter and existing cookies, only called when Osano consent is set to allow.
 * @see https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/fbp-and-fbc/
 * @param request
 * @param response
 */
export function setFacebookCookiesMiddleware(request: NextRequest, response: NextResponse): void {
  const { searchParams } = request.nextUrl;
  const fbclid = searchParams.get(CLICK_ID_QUERY_PARAM_NAME);
  const ninetyDaysFromNow = new Date(Date.now() + NINETY_DAYS_MS);

  if (fbclid) {
    const existingFbc = request.cookies.get(CLICK_ID_COOKIE_NAME)?.value;
    const shouldSetFbc = !existingFbc || getFbcFbclid(existingFbc) !== fbclid;

    if (shouldSetFbc) {
      response.cookies.set({
        name: CLICK_ID_COOKIE_NAME,
        value: buildFbcValue(fbclid),
        expires: ninetyDaysFromNow,
        domain: process.env.APEX_DOMAIN!,
        secure: true
      });
    }
  }

  if (!request.cookies.has(BROWSER_ID_COOKIE_NAME)) {
    response.cookies.set({
      name: BROWSER_ID_COOKIE_NAME,
      value: buildFbpValue(),
      expires: ninetyDaysFromNow,
      domain: process.env.APEX_DOMAIN!,
      secure: true
    });
  }
}

/**
 * Client-side counterpart to `setFacebookCookiesMiddleware`.
 *
 * @param apexDomain - Apex domain to scope the cookie to, so it is readable across subdomains.
 * @see https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/fbp-and-fbc/
 */
export const setFacebookCookiesClientSide = (apexDomain: string): void => {
  const fbclid = new URLSearchParams(window.location.search).get(CLICK_ID_QUERY_PARAM_NAME);
  const existingFbc = Cookies.get(CLICK_ID_COOKIE_NAME);
  const existingFbp = Cookies.get(BROWSER_ID_COOKIE_NAME);

  if (fbclid && !(existingFbc && getFbcFbclid(existingFbc) === fbclid)) {
    Cookies.set(CLICK_ID_COOKIE_NAME, buildFbcValue(fbclid), marketingCookieAttributes(apexDomain));
  }

  if (!existingFbp) {
    Cookies.set(BROWSER_ID_COOKIE_NAME, buildFbpValue(), marketingCookieAttributes(apexDomain));
  }
};

// Private

const buildFbcValue = (fbclid: string) => `fb.1.${Date.now()}.${fbclid}`;

const buildFbpValue = () => {
  const randomId = Math.floor(Math.random() * 2147483648); // 2^31
  return `fb.1.${Date.now()}.${randomId}`;
};

const initFacebookPixel = (userId: number, emailSha256?: string | null) => {
  fbq('init', process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID, {
    external_id: userId,
    ...(emailSha256 && { em: emailSha256 })
  });
};

// Check for presence of fbq in case of content blockers
const isFacebookPixelUnavailable = () => {
  return typeof fbq === 'undefined';
};

const getFbcFbclid = (fbc: string): string | undefined => {
  // Format: fb.1.<timestamp>.<fbclid>
  // fbclid may contain dots, so rejoin everything after the third dot
  const parts = fbc.split('.');
  if (parts.length < 4) {
    return undefined;
  }
  return parts.slice(3).join('.');
};
